import type { ElementNode } from '@payloadcms/richtext-slate';
import * as RadixAccordion from '@radix-ui/react-accordion';
import classNames from 'classnames';
import { ChevronLeft } from 'lucide-react';

import { PayloadSlateRenderer } from '@/components/PayloadSlateRenderer/PayloadSlateRenderer';
import type { AccordionBlock } from '@/types';
import { slugify } from '@/util/strings/slugify';

import { TrackedLink } from './TrackedLink';

import styles from './Accordion.module.scss';

export function Accordion({
    items,
    openedIndex,
    setOpenedIndex,
    allowClosingLastItem,
}: Accordion.Props) {
    return (
        <RadixAccordion.Root type="single" value={String(openedIndex)}>
            {items?.map(({ content, title, id, link, has_link: hasLink }, index) => (
                <RadixAccordion.Item
                    value={String(index)}
                    className={styles.container}
                    key={id}
                    data-payload-accordion-item={id}
                >
                    <RadixAccordion.Trigger
                        className={styles.button}
                        onClick={() => {
                            if (openedIndex !== index) {
                                setOpenedIndex(index);
                            } else if (allowClosingLastItem && openedIndex === index) {
                                setOpenedIndex(undefined);
                            }
                        }}
                    >
                        <span data-payload-accordion-item-title={slugify(title)}>{title}</span>
                        <ChevronLeft
                            className={classNames(styles.arrow, {
                                [styles.open]: openedIndex === index,
                            })}
                        />
                    </RadixAccordion.Trigger>

                    <RadixAccordion.Content className={styles.bodyWrapper}>
                        <div className={styles.body} data-payload-accordion-item-content>
                            {content && (
                                <PayloadSlateRenderer
                                    data-payload-accordion-item-body
                                    nodes={content as ElementNode[]}
                                />
                            )}
                            {hasLink && link && <TrackedLink link={link} />}
                        </div>
                    </RadixAccordion.Content>
                </RadixAccordion.Item>
            ))}
        </RadixAccordion.Root>
    );
}

export namespace Accordion {
    export interface Props {
        items: AccordionBlock['items'];
        openedIndex: number | undefined;
        setOpenedIndex: (index: number | undefined) => void;
        allowClosingLastItem?: boolean;
    }
}
